import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom'

type NavigateMethod = (to: unknown, ...rest: unknown[]) => void
type GuardedNavigator = { push: NavigateMethod; replace: NavigateMethod }

const toPathname = (to: unknown) => {
  if (typeof to === 'string') return new URL(to, window.location.href).pathname
  return (to as { pathname?: string } | null)?.pathname || window.location.pathname
}

const historyIndex = (): number | undefined => window.history.state?.idx

let popStateGuard: ((event: PopStateEvent) => void) | undefined
// Registered at import so it runs before the router's own popstate listener: Chrome calls listeners on `window` in
// registration order regardless of capture, and React commits the router's navigation before the next listener runs.
window.addEventListener('popstate', (event) => popStateGuard?.(event), true)

/**
 * While `when` is true, asks before the page is left: reload and closing the tab (the browser's own prompt),
 * links and `navigate` to another path, and browser back/forward. Changes of the query string only pass.
 * `<BrowserRouter>` has no `useBlocker`, so in-app navigation is held by wrapping the router's navigator, and a
 * back/forward pop is undone before the router sees it and replayed by `proceed`.
 */
export const useUnsavedChangesGuard = (when: boolean) => {
  const { navigator } = useContext(UNSAFE_NavigationContext)
  const location = useLocation()
  const [pending, setPending] = useState<{ go: () => void }>()
  const pathnameRef = useRef(location.pathname)
  const indexRef = useRef(historyIndex())
  const allowRef = useRef(false)

  useEffect(() => {
    pathnameRef.current = location.pathname
    indexRef.current = historyIndex()
    allowRef.current = false
  }, [location])

  useEffect(() => {
    if (!when) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [when])

  useEffect(() => {
    if (!when) return
    const guarded = navigator as unknown as GuardedNavigator
    const { push, replace } = guarded
    const guard =
      (method: NavigateMethod): NavigateMethod =>
      (to, ...rest) => {
        const go = () => method.apply(guarded, [to, ...rest])
        if (allowRef.current || toPathname(to) === pathnameRef.current) return go()
        setPending({ go })
      }
    guarded.push = guard(push)
    guarded.replace = guard(replace)
    return () => {
      guarded.push = push
      guarded.replace = replace
    }
  }, [navigator, when])

  useEffect(() => {
    if (!when) return
    let revertingPop = false
    const onPopState = (event: PopStateEvent) => {
      if (revertingPop) {
        revertingPop = false
        event.stopImmediatePropagation()
        return
      }
      if (allowRef.current || window.location.pathname === pathnameRef.current) return
      const nextIndex = historyIndex()
      const currentIndex = indexRef.current
      // Entries the router didn't create have no index, so the step can't be undone.
      if (nextIndex === undefined || currentIndex === undefined) return
      event.stopImmediatePropagation()
      const delta = currentIndex - nextIndex
      revertingPop = true
      window.history.go(delta)
      setPending({ go: () => window.history.go(-delta) })
    }
    popStateGuard = onPopState
    return () => {
      if (popStateGuard === onPopState) popStateGuard = undefined
    }
  }, [when])

  const proceed = useCallback(() => {
    if (!pending) return
    setPending(undefined)
    allowRef.current = true
    pending.go()
  }, [pending])
  const cancel = useCallback(() => setPending(undefined), [])

  return { isBlocked: !!pending, proceed, cancel }
}

export type UnsavedChangesGuard = ReturnType<typeof useUnsavedChangesGuard>
